import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  TextField,
  Button,
  MenuItem,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer
} from "@mui/material";

import api from "../../api/api";

export default function WorkItemDetail() {

  const navigate = useNavigate();
  
  const { id } = useParams();   // ⭐ ADD THIS LINE

  const [item, setItem] = useState(null);
  const [team, setTeam] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");

  /* ============================================
     WORK LOG STATES
  ============================================ */

  const [minutes, setMinutes] = useState("");
  const [workComment, setWorkComment] = useState("");
  const [workLogs, setWorkLogs] = useState([]);

  /* NEW: allow Admin/HR to select user */
  const [selectedUser, setSelectedUser] = useState("");

  /* Allow logging work for previous dates */
  const [logDate, setLogDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {

      const itemRes = await api.get(`/work-items/${id}`);
      const workItem = itemRes.data;
      setItem(workItem);

      const commentsRes = await api.get(`/work-items/${id}/comments`);
      setComments(commentsRes.data);

      if (workItem.project_id) {
        const teamRes = await api.get(
          `/projects/${workItem.project_id}/team-users`
        );
        setTeam(teamRes.data);
      }

      /* Load work logs */
      const logRes = await api.get(`/work-items/${id}/work-logs`);
      setWorkLogs(logRes.data);

    } catch (err) {
      console.error("Error loading work item", err);
    }
  };

  /* ============================================
     STATUS UPDATE
  ============================================ */

  const updateStatus = async (status) => {
    try {
      await api.patch(`/work-items/${id}/status`, { status });
      loadData();
    } catch (err) {
      console.error("Error updating status", err);
    }
  };

  /* ============================================
     ASSIGN USER
  ============================================ */

  const assignUser = async (userId) => {
    try {
      await api.patch(`/work-items/${id}/assign`, {
        assignee_id: userId || null
      });
      loadData();
    } catch (err) {
      console.error("Error assigning user", err);
    }
  };

  /* ============================================
     ADD COMMENT
  ============================================ */

  const addComment = async () => {

    if (!newComment.trim()) return;

    try {

      await api.post(`/work-items/${id}/comments`, {
        comment: newComment
      });

      setNewComment("");
      loadData();

    } catch (err) {
      console.error("Error adding comment", err);
    }
  };

  /* ============================================
     LOG WORK
  ============================================ */

  const logWork = async () => {

    if (!minutes) return;

    try {

      await api.post(`/work-logs`, {
        work_item_id: id,
        minutes: parseInt(minutes),
        comment: workComment,
        log_date: logDate,

        /* NEW: Admin/HR can log work for another user */
        user_id: selectedUser || null
      });

      setMinutes("");
      setWorkComment("");

      loadData();

    } catch (err) {
      console.error("Error logging work", err);
    }
  };

  if (!item) return <Typography p={3}>Loading...</Typography>;

  return (
    <Box p={4}>
      <Grid container spacing={3}>

        {/* LEFT SIDE */}

        <Grid item xs={8}>

         {/* Work Item Code (Clickable) */}
<Box display="flex" alignItems="center" mb={2}>

  {/* Work Item Code */}
  <Chip
  label={item.work_item_code}
  clickable
  onClick={() => navigate(`/projects/${item.project_id}`)}
  sx={{
    mr: 1,
    fontWeight: 600,
    cursor: "pointer",

    // 🎯 Jira-like look
    backgroundColor: "#fdecea",
    color: "#d32f2f",

    borderRadius: "16px",
    px: 1,

    // ✨ Smooth transition
    transition: "all 0.2s ease",

    // 🖱️ Hover effect
    "&:hover": {
      textDecoration: "underline",
      backgroundColor: "#f8d7da",
      transform: "scale(1.05)"
    }
  }}
/>

  {/* Title */}
  <Typography variant="h5">
    {item.title}
  </Typography>

</Box>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography>
                {item.description || "No description"}
              </Typography>
            </CardContent>
          </Card>

          {/* COMMENTS */}

          <Typography variant="h6" mb={2}>
            Comments
          </Typography>

          {comments.map((c) => (
            <Card key={c.id} sx={{ mb: 1 }}>
              <CardContent>

                <Typography fontWeight="bold">
                  {c.user}
                </Typography>

                <Typography sx={{ whiteSpace: "pre-line" }}>
                  {c.comment}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {c.created_at}
                </Typography>

              </CardContent>
            </Card>
          ))}

          {/* COMMENT INPUT */}

          <Card sx={{ p: 2, mt: 2 }}>
            <Grid container spacing={2}>

              <Grid item xs={10}>

                <TextField
                  multiline
                  minRows={1}
                  maxRows={6}
                  fullWidth
                  placeholder="Write a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.ctrlKey) {
                      addComment();
                    }
                  }}
                />

              </Grid>

              <Grid item xs={2}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ height: "56px" }}
                  onClick={addComment}
                >
                  Add
                </Button>
              </Grid>

            </Grid>
          </Card>

          {/* LOG WORK */}

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" mb={2}>
            Log Work
          </Typography>

          <Card sx={{ p: 2 }}>

            <Grid container spacing={2} alignItems="center">

              {/* =====================================
                 NEW: USER LOV (Admin / HR)
              ===================================== */}

              <Grid item xs={3}>
                <TextField
                  select
                  fullWidth
                  label="User"
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                >

                  <MenuItem value="">
                    Logged In User
                  </MenuItem>

                  {team.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name}
                    </MenuItem>
                  ))}

                </TextField>
              </Grid>

              {/* Date */}

              <Grid item xs={3}>
                <TextField
                  type="date"
                  label="Date"
                  fullWidth
                  value={logDate}
                  onChange={(e) => setLogDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Minutes */}

              <Grid item xs={2}>
                <TextField
                  label="Minutes"
                  value={minutes}
                  type="text"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      setMinutes(value);
                    }
                  }}
                  fullWidth
                />
              </Grid>

              {/* Comment */}

              <Grid item xs={3}>
                <TextField
                  multiline
                  minRows={1}
                  maxRows={6}
                  fullWidth
                  label="Work Comment"
                  value={workComment}
                  onChange={(e) => setWorkComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.ctrlKey) {
                      logWork();
                    }
                  }}
                />
              </Grid>

              {/* Button */}

              <Grid item xs={1}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ height: "56px" }}
                  onClick={logWork}
                >
                  Log
                </Button>
              </Grid>

            </Grid>

          </Card>

          {/* WORK LOG HISTORY TABLE */}

          <Typography variant="subtitle1" mt={3} mb={1}>
            Work Log History
          </Typography>

          {workLogs.length === 0 ? (

            <Typography color="text.secondary">
              No work logged yet.
            </Typography>

          ) : (

            <TableContainer component={Card}>

              <Table size="small">

                <TableHead>
                  <TableRow>
                    <TableCell><b>Date</b></TableCell>
                    <TableCell><b>User</b></TableCell>
                    <TableCell align="right"><b>Minutes</b></TableCell>
                    <TableCell><b>Comment</b></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>

                  {workLogs.map((log) => (

                    <TableRow key={log.id}>

                      <TableCell>{log.date}</TableCell>

                      <TableCell>{log.user}</TableCell>

                      <TableCell align="right">{log.minutes}</TableCell>

                      <TableCell sx={{ whiteSpace: "pre-line" }}>
                        {log.comment}
                      </TableCell>

                    </TableRow>

                  ))}

                </TableBody>

              </Table>

            </TableContainer>

          )}

        </Grid>

        {/* RIGHT PANEL */}

        <Grid item xs={4}>
          <Card>
            <CardContent>

              <Typography variant="subtitle2">
                Status
              </Typography>

              <TextField
                select
                fullWidth
                value={item.status}
                onChange={(e) => updateStatus(e.target.value)}
                sx={{ mt: 1 }}
              >
                <MenuItem value="OPEN">OPEN</MenuItem>
                <MenuItem value="IN_PROGRESS">IN_PROGRESS</MenuItem>
                <MenuItem value="DONE">DONE</MenuItem>
              </TextField>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2">
                Assign To
              </Typography>

              <TextField
                select
                fullWidth
                value={item.assignee?.id || ""}
                onChange={(e) => assignUser(e.target.value)}
                sx={{ mt: 1 }}
              >
                <MenuItem value="">Unassigned</MenuItem>

                {team.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}

              </TextField>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2">
                Priority
              </Typography>

              <Chip label={item.priority} sx={{ mt: 1 }} />

            </CardContent>
          </Card>
        </Grid>

      </Grid>
    </Box>
  );
}